/**
 * Single source of truth for all user-facing static text.
 *
 * Components import this and expose it to their template, e.g.:
 *
 *   protected readonly T = TEXT;
 *
 * Then templates use `{{ T.login.signIn }}` etc.
 *
 * Dynamic content (user names, API data) is NOT defined here.
 */
export const TEXT = {
  // ─────────────────────────────────────────────────────────────────────
  // Common / shared
  // ─────────────────────────────────────────────────────────────────────
  common: {
    appName: 'OmniFlow',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    continue: 'Continue',
    save: 'Save',
    submit: 'Submit',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    search: 'Search',
    clear: 'Clear',
    export: 'Export',
    exporting: 'Exporting...',
    loading: 'Loading...',
    enabled: 'Enabled',
    disabled: 'Disabled',
    open: 'Open',
    nextStepEyebrow: 'NEXT STEP',
    selectLocation: 'Select Location',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Login + sign-in flow
  // ─────────────────────────────────────────────────────────────────────
  login: {
    brandPartner: 'Assurant',
    headlineLine1: 'Operational control,',
    headlineLine2: 'simplified.',
    subtitle:
      'A unified platform for receiving, item processing, and distribution center operations.',
    bullets: [
      'Standardized receiving workflows',
      'Real-time item and package visibility',
    ] as const,
    signInHeading: 'Sign in',
    signInButton: 'Sign In',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Auth-check splash (login-landing)
  // ─────────────────────────────────────────────────────────────────────
  loginLanding: {
    signingYouIn: 'Signing you in…',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Sidebar navigation
  // ─────────────────────────────────────────────────────────────────────
  sidebar: {
    closeMenuAria: 'Close menu',
    closeBackdropAria: 'Close navigation menu',
    signOut: 'Sign Out',
    signOutTitle: 'Sign out of OmniFlow',
    locationLockedTitle: 'Select a location from the Dashboard first',
    sections: {
      management: 'Management',
      dockReceiving: 'Dock Receiving',
      itemReceiving: 'Device Processing',
      userManagement: 'User Management',
      configuration: 'Configuration',
    },
    items: {
      dashboard: 'Dashboard',
      receivePackages: 'Receive Packages',
      search: 'Search',
      receiveItems: 'Device Photo',
      secondaryClearDashboard: 'Secondary Clear Dashboard',
      secondaryClearProcessing: 'Secondary Clear Processing',
      userList: 'User List',
      dccConfiguration: 'DCC Configuration',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Dashboard / location picker
  // ─────────────────────────────────────────────────────────────────────
  dashboard: {
    title: 'Management Dashboard',
    subtitle: 'Select the warehouse location to begin.',
    selectedLocationEyebrow: 'Selected Location',
    selectedPlaceholder: '—',
    selectedDescription:
      'Continue to start the dock receiving workflow for the selected warehouse.',
    noSelectionDescription: 'Choose a warehouse on the left to continue.',
    continue: 'Continue',
    emptyTitle: 'No locations assigned',
    emptyBody:
      'Contact your administrator to get access to a distribution center.',
    noAccessibleFeatures:
      'You do not have access to any features for this location.',
  },

  // ─────────────────────────────────────────────────────────────────────
  // DCC Configuration
  // ─────────────────────────────────────────────────────────────────────
  dccConfig: {
    title: 'DCC Configuration',
    subtitle:
      'Manage feature availability and warehouse assignment across distribution centers',
    addFeature: 'Add Feature',
    emptyTitle: 'No features configured yet',
    emptyBody: 'Click "Add Feature" above to create your first feature flag',
    selectDcc: 'Select DCC',
    selectDccLocation: 'Select a DCC location',
    assign: 'Assign',
    noChips: 'No DCCs assigned — use the selector to add one',
    dialog: {
      title: 'New Feature Flag',
      subtitle: 'Define a feature flag and assign DCC locations.',
      closeAria: 'Close dialog',
      sectionDetails: 'Feature Details',
      sectionGroups: 'DCC Groups',
      featureName: 'Feature Name',
      description: 'Description',
      enabledToggle: 'Enable this feature on save',
      enabledToggleDesc: 'Feature will be active immediately after saving.',
      dccGroups: 'DCC Groups',
      dccGroupsHelper:
        'Choose one or more distribution centers for this feature.',
      addGroup: 'Add Group',
      removeGroupAria: 'Remove Group',
      saveButton: 'Save Feature',
      featureNamePlaceholder: 'e.g. Dock Entry, Returns Flow, Item Scan…',
      descriptionPlaceholder:
        'Short description of what this feature controls…',
      requiredMark: '*',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // User List (User Management)
  // ─────────────────────────────────────────────────────────────────────
  userList: {
    title: 'User List',
    subtitle: 'Manage platform users, roles, and distribution center access.',
    addUser: 'Add User',
    searchPlaceholder: 'Search users',
    allRoles: 'All Roles',
    allLocations: 'All Locations',
    columns: {
      user: 'USER',
      email: 'EMAIL',
      dccs: 'DCCS',
      permissions: 'PERMISSIONS',
      actions: 'ACTIONS',
    },
    emptyDccs: '—',
    pager: {
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page',
    },
    emptyStateTitle: 'No users found',
    emptyStateBody:
      'Try adjusting your filters or click "Add User" to invite someone.',
    deleteDialog: {
      title: 'Delete User',
      messagePrefix: 'Are you sure you want to delete',
      messageSuffix: '? This action cannot be undone.',
      confirm: 'Delete',
    },
    tile: {
      noDccs: 'No DCCs assigned',
    },
    viewToggle: {
      table: 'Table view',
      tile: 'Tile view',
    },
    detailsDialog: {
      addTitle: 'Add User',
      editTitle: 'Edit User',
      addSubtitle: 'Add a new user with permissions and DCC access.',
      editSubtitle: 'Update user information, permissions, and access.',
      closeAria: 'Close dialog',
      contactInfo: 'Contact Info',
      firstName: 'First Name',
      firstNamePlaceholder: 'e.g. John',
      firstNameRequired: 'First Name is required',
      lastName: 'Last Name',
      lastNamePlaceholder: 'e.g. Smith',
      lastNameRequired: 'Last Name is required',
      email: 'Email',
      emailPlaceholder: 'e.g. john@company.com',
      emailRequired: 'Email is required',
      administrator: 'Administrator',
      administratorDescription:
        'Grant full admin privileges and system access.',
      dccPermissions: 'DCC Permissions',
      dccPermissionsHelper: 'Select the DCCs this user has access to.',
      functionalPermissions: 'Functional Permissions',
      functionalPermissionsHelper: 'Select the features this user can access.',
      clientProgramAccess: 'Customer / Program Access',
      clientProgramAccessHelper: 'Select Customer-Program access for this user',
      clientProgramSearchPlaceholder: 'Search by client or contract code...',
      clientProgramSelectedSuffix: 'selected',
      clientProgramNoResults: 'No matching client or contract.',
      saveButton: 'Save Changes',
      saveButtonAdd: 'Add User',
      savingButton: 'Saving...',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Questionnaire (Dock Receiving step 2)
  // ─────────────────────────────────────────────────────────────────────
  questionnaire: {
    pageTitle: 'Dock Receiving',
    intakeEyebrow: 'PACKAGE INTAKE',
    intakeTitle: 'Package Material Questionnaire',
    barcodeLabel: 'BARCODE',
    identifierLabel: 'RMA NUMBER / TRACKING NUMBER',
    questionPrefix: 'Question',
    questionOf: 'of',
    requiredError:
      'A response is needed for this question. Please provide an answer to continue.',
    answerAllRequired:
      'Please answer every question before submitting. No question may be left blank.',
    textAnswerPlaceholder: 'Type your answer here…',
    needHelpDefault: 'Need help?',
    viewGuideDefault: 'View Guide',
    guideModal: {
      closeAria: 'Close guide',
      importantLabel: 'IMPORTANT',
      statusCorrect: 'CORRECT',
      statusIncorrect: 'NOT CORRECT',
      statusConditional: 'CORRECT IF BOX HAS BOTH',
    },
    previousButton: 'Previous',
    nextButton: 'Next',
    errorState:
      'No questionnaire found for this package or failed to load questionnaire.',
    loadFailedFallback: 'An error occurred while loading the questionnaire.',
    confirmSuccess: 'Dock receipt confirmed successfully.',
    confirmFailure: 'Failed to confirm dock receipt.',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Dock Receiving — Scan step
  // ─────────────────────────────────────────────────────────────────────
  dockReceiving: {
    pageTitle: 'Dock Receiving',
    cardTitle: 'Receive a Package',
    cardSubtitle:
      'You only need one identifier — enter a tracking number or an RMA number to continue.',
    sectionTitle: 'Enter One Identifier',
    sectionSubtitle: 'You only need to enter one field to continue',
    trackingLabel: 'TRACKING NUMBER',
    trackingPlaceholder: 'Enter tracking number',
    trackingHelper: 'Enter the full tracking number.',
    orDivider: 'OR',
    rmaLabel: 'RMA NUMBER',
    rmaPlaceholder: 'Enter RMA number',
    rmaHelper: 'Enter an RMA Number if provided.',
    inputPlaceholder: 'Scan or enter barcode here',
    info: 'Barcode scanners can submit directly into the input field for faster receiving.',
    nextStepDescription:
      'Proceed to package validation and questionnaire after scan.',
    submitButton: 'Receive Package',
    clearButton: 'Clear',
    errorNotFound: 'Barcode not found. Check the label or scan again.',
    errorRmaNotFound: 'RMA not found. Check the number or scan again.',
    stepIndicator: {
      scan: 'Scan',
      questionnaire: 'Questionnaire',
      summary: 'Summary',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Item Receiving — IMEI step + others
  // ─────────────────────────────────────────────────────────────────────
  itemReceiving: {
    pageTitle: 'Device Receiving',
    imei: {
      cardTitle: 'Scan or enter IMEI / Serial Number',
      cardSubtitle:
        'Scan each item barcode individually to validate product details and continue item intake.',
      inputPlaceholder: 'Scan or enter item barcode here',
      info: 'Each item should be scanned separately to ensure accurate receiving and inventory tracking.',
      nextStepDescription:
        'Review item details and continue to confirmation after each successful scan.',
      submitButton: 'Take Photos',
      errorRequired: 'Enter or scan an IMEI / serial number to continue.',
    },
    capture: {
      eyebrow: 'DEVICE RECEIVING',
      heading: 'Device Condition & Photos',
      subtitle:
        'Capture the device condition and photo evidence before moving to annotation.',
      imeiLabel: 'IMEI',
      imeiPrefix: 'IMEI :',
      datePrefix: 'Date :',
      conditionLabel: 'Condition assessment',
      conditionSubtitle: 'Select one condition grade to continue.',
      conditionRequired: 'Required',
      conditionSelected: 'Selected:',
      photosLabel: 'Photo evidence',
      photosSubtitle: 'Multiple angles are recommended for audit quality.',
      capturedSuffix: 'captured',
      capturedPreviews: 'Captured previews',
      takePhoto: 'Take Photo',
      useCamera: 'Use camera',
      upload: 'Upload',
      fromFiles: 'From files',
      multipleAnglesHint: 'Multiple angles recommended',
      annotateButton: 'Annotate Photos',
      backButton: 'Back',
      conditions: {
        excellent: { label: 'Excellent', desc: 'No visible wear' },
        good: { label: 'Good', desc: 'Minor scratches' },
        fair: { label: 'Fair', desc: 'Visible scratches' },
        damaged: { label: 'Damaged', desc: 'Significant damage' },
      },
      camera: {
        heading: 'Take Photos',
        capturedSuffix: 'captured',
        closeAria: 'Close camera',
        done: 'Done',
        capture: 'Capture',
      },
    },
    annotation: {
      heading: 'Annotate',
      devicePhotoLabel: 'Device photo',
      hintWithTool:
        'Click on the image to place a marker — or drag existing markers to reposition',
      hintWithoutTool: 'Select a tool above, then click on the image',
      clearAll: 'Clear All',
      capturedPhotos: 'CAPTURED PHOTOS',
      marksSuffix: 'marks',
      reviewButton: 'Review',
      backButton: 'Back',
      tools: {
        scratch: 'Scratch',
        dent: 'Dent',
        crack: 'Crack',
      },
    },
    summary: {
      eyebrow: 'DEVICE RECEIVING',
      heading: 'Review & Submit',
      subtitle: 'Confirm details and submit. You can edit any section.',
      readyBadge: 'Ready',
      readyBadgeLong: 'Ready for submission',
      summaryTitle: 'Summary',
      annotatedPhotosTitle: 'Annotated Photos',
      annotatedPhotosSubtitle: 'Per-angle annotation summary',
      submissionTitle: 'Submission',
      submissionStatus: 'Status',
      actionsTitle: 'Actions',
      backToAnnotate: 'Back to Annotate',
      editLink: 'Edit',
      labelImei: 'IMEI',
      labelCondition: 'Condition',
      labelPhotos: 'Photos',
      labelAnnotations: 'Annotations',
      annotatedPhotos: 'Annotated Photos',
      notesLabel: 'Notes',
      notesOptional: 'Optional',
      notesPlaceholder: 'Add any additional notes about this device…',
      uploadingButton: 'Uploading…',
      submitButton: 'Submit',
      successHeading: 'Submitted!',
      successMessagePrefix: 'Device',
      successMessageSuffix: 'Photos have been submitted successfully.',
      receiveAnother: 'Scan Next IMEI',
      uploadFailedFallback: 'Upload failed. Please try again.',
      marksSuffix: 'marks',
    },
    stepIndicator: {
      imei: 'IMEI',
      capture: 'Capture',
      annotate: 'Annotate',
      review: 'Review',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Secondary Clear Processing
  // ─────────────────────────────────────────────────────────────────────
  secondaryClear: {
    pageTitle: 'Secondary Clear Processing',
    fields: {
      customerProgram: 'Customer / Program',
      customerProgramPlaceholder: 'Select Customer / Program',
      customerProgramLoading: 'Loading…',
      ceItemNumber: 'CE Item Number',
      serialNumber: 'Serial Number',
      levelOfProcessing: 'Level of Processing',
      failureReason: 'Failure Reason',
      failureReasonPlaceholder: 'Type code or description to search',
    },
    levels: {
      level1: 'Level 1',
      level2: 'Level 2',
      level3: 'Level 3',
      level4: 'Level 4',
    },
    actions: {
      clear: 'Clear',
      submit: 'Submit',
      submitting: 'Submitting…',
    },
    errors: {
      required: 'Required',
      ceItemFormat: 'CE Item Number must be 9 digits and start with 708',
      serialFormat: 'Serial Number must be exactly 15 digits',
      submitFailed: 'Submission failed. Please try again.',
      noFailureReasonMatches: 'No matching failure reasons.',
      noCustomerProgramMatches: 'No matching Customer / Program.',
      clientContractsLoadFailed:
        'Failed to load Customer / Program list. Please refresh and try again.',
      noClientContractsAssigned:
        'No Customer / Program is assigned to your account. Contact your administrator.',
    },
    toast: {
      success: 'Secondary clear recorded successfully.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Search Dock (search results page)
  // ─────────────────────────────────────────────────────────────────────
  searchDock: {
    pageTitle: 'Search Packages',
    filtersHeading: 'FILTERS',
    fields: {
      tracking: 'TRACKING NUMBER',
      trackingPlaceholder: 'Enter tracking number',
      rma: 'RMA NUMBER',
      rmaPlaceholder: 'Enter RMA number',
      customer: 'CUSTOMER',
      customerPlaceholder: 'Enter customer name',
      status: 'STATUS',
      statusPlaceholder: 'All statuses',
      dateRange: 'DOCK SCAN DATE AND TIME',
      dateRangePlaceholder: 'Choose date & time range',
    },
    statusOptions: [
      { label: 'Dock Received', value: 'Dock Received' },
      { label: 'Dock Reject', value: 'Dock Reject' },
      { label: 'Dock Processing', value: 'Dock Processing' },
    ],
    actions: {
      search: 'Search',
      clear: 'Clear',
      export: 'Export',
      exporting: 'Exporting...',
    },
    receivedPackages: 'Received Packages',
    shownSuffix: 'shown',
    columns: {
      tracking: 'Tracking #',
      status: 'Status',
      rma: 'RMA',
      clientProgram: 'Client / Program',
      scannedBy: 'Scanned By',
      date: 'Dock Scan Date and Time',
      actions: 'Actions',
    },
    editQuestionnaire: 'Edit Questionnaire',
    emptyTitle: 'No records found',
    emptyBody:
      'No packages match your search criteria. Try adjusting your filters.',
    rmaPrefix: 'RMA:',
    questionsAnsweredSuffix: 'questions answered',
    popover: {
      summaryPrefix: 'Summary — Row',
      noQuestions: 'No questions found',
    },
    viewToggle: {
      tile: 'Tile view',
      table: 'Table view',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Secondary Clear Dashboard
  // ─────────────────────────────────────────────────────────────────────
  secondaryClearDashboard: {
    pageTitle: 'Secondary Clear Processing Dashboard',
    subtitle:
      'Manager view of processed devices, repair mix, and failure reason trends.',
    filtersHeading: 'FILTERS',
    fields: {
      dateRange: 'DATE',
      dateRangePlaceholder: 'Choose date range',
      ceItemNumber: 'CE ITEM NUMBER',
      ceItemNumberPlaceholder: 'Enter CE item number',
      levelOfProcessing: 'LEVEL OF PROCESSING',
      levelOfProcessingPlaceholder: 'Choose level(s)',
      failureReason: 'FAILURE REASON',
      failureReasonPlaceholder: 'Choose failure reason(s)',
      customerProgram: 'CUSTOMER / PROGRAM',
      customerProgramPlaceholder: 'Choose customer/program',
      user: 'USER',
      userPlaceholder: 'Enter user',
    },
    actions: {
      search: 'Search',
      clear: 'Clear',
      export: 'Export',
      exporting: 'Exporting...',
    },
    metrics: {
      devicesProcessed: 'Devices Processed',
    },
    charts: {
      devicesByLevel: 'Devices by Level of Processing',
      devicesByFailureReason: 'Devices by Failure Reason',
      placeholder: 'Charts coming soon',
    },
    processedDevices: 'Processed Devices',
    shownSuffix: 'shown',
    columns: {
      processedDate: 'Processed Date',
      ceItemNumber: 'CE Item Number',
      serialNumber: 'Serial Number',
      customerProgram: 'Customer / Program',
      level: 'Level of Processing',
      failureReason: 'Failure Reason',
      user: 'User',
    },
    emptyTitle: 'No records found',
    emptyBody:
      'No processed devices match your search criteria. Try adjusting your filters.',
    selectDateTitle: 'Select a date range',
    selectDateBody:
      'Choose a date range and click Search to view processed devices.',
    errors: {
      dateRangeRequired: 'Date is required',
    },
    viewToggle: {
      tile: 'Tile view',
      table: 'Table view',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // User Profile
  // ─────────────────────────────────────────────────────────────────────
  userProfile: {
    pageTitle: 'User Profile',
    fields: {
      name: 'Name',
      email: 'Email',
      theme: 'Theme',
    },
    back: 'Back',
    signOut: 'Sign Out',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Returns Receiving
  // ─────────────────────────────────────────────────────────────────────
  returnsReceiving: {
    pageTitle: 'Returns Receiving',
    subtitle: 'Process return shipments here',
    emptyMessage: 'No returns to process at the moment',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Access Denied
  // ─────────────────────────────────────────────────────────────────────
  accessDenied: {
    pageTitle: 'Access Denied',
  },
} as const;

export type AppText = typeof TEXT;
